import type { ReactElement } from "react";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ChevronLeft, ChevronRight, List } from "lucide-react";
import PageLayout from "../components/PageLayout";

/**
 * Terms section
 */
interface TermsSection {
  id: string;
  title: string;
  shortTitle: string;
  content: string;
}

const LAST_UPDATED = "January 20, 2026";

const SECTIONS: TermsSection[] = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    shortTitle: "Acceptance",
    content: `By accessing or using the Mytho Labs service ("Service"), including our website and applications, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.

These Terms constitute a legally binding agreement between you and Mytho Labs ("Company," "we," "us," or "our"). We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the modified Terms.`,
  },
  {
    id: "description",
    title: "2. Description of Service",
    shortTitle: "Service",
    content: `Mytho Labs provides an AI-powered platform for creating personalized illustrated storybooks. Our Service includes:

• AI-generated story text based on user prompts
• AI-generated illustrations for stories
• Audio narration features (for Premium users)
• Story storage and sharing capabilities
• Book Series creation for recurring characters

The Service is provided "as is" and we make no guarantees regarding availability, accuracy, or specific outcomes of AI-generated content.`,
  },
  {
    id: "accounts",
    title: "3. User Accounts",
    shortTitle: "Accounts",
    content: `To access certain features, you must create an account. You agree to:

• Provide accurate, current, and complete information
• Maintain the security of your password
• Accept responsibility for all activities under your account
• Notify us immediately of any unauthorized access

You must be at least 13 years old to create an account. Users under 18 require parental or guardian consent. Parents and guardians are responsible for supervising children's use of the Service.`,
  },
  {
    id: "content-conduct",
    title: "4. User Content and Conduct",
    shortTitle: "Content",
    content: `You are responsible for all content you submit as prompts or inputs to our Service ("User Content"). You agree not to submit content that:

• Is illegal, harmful, threatening, abusive, or harassing
• Contains explicit sexual or violent content
• Infringes on intellectual property rights
• Contains personal information of minors without consent
• Attempts to circumvent content safety measures
• Is designed to harm or exploit children

We reserve the right to refuse to generate stories or remove content that violates these guidelines.`,
  },
  {
    id: "ai-content",
    title: "5. AI-Generated Content",
    shortTitle: "AI Content",
    content: `Content generated by our AI systems ("Generated Content") is created algorithmically. You understand that:

• AI outputs may be unpredictable and not always accurate
• Generated Content may occasionally contain errors or inconsistencies
• We do not guarantee any specific results or outcomes
• Generated Content should not be relied upon for factual information
• Similar prompts from different users may produce similar content

We employ safety measures to prevent inappropriate content generation, but no system is perfect. Please report any concerning outputs immediately.`,
  },
  {
    id: "ip",
    title: "6. Intellectual Property",
    shortTitle: "IP Rights",
    content: `Ownership of Generated Content:
Subject to these Terms, you own the Generated Content created using your prompts, provided you have an active account in good standing. This ownership is subject to:

• Our license to use, display, and process content as needed to provide the Service
• Any applicable third-party rights
• Our right to use anonymized data to improve our services

Our Intellectual Property:
The Service, including its software, design, logos, and trademarks, remains the property of Mytho Labs. You may not copy, modify, or reverse engineer any part of our Service.`,
  },
  {
    id: "payments",
    title: "7. Payments and Subscriptions",
    shortTitle: "Payments",
    content: `Credit Purchases:
• Credits are purchased in advance and used to generate stories
• Purchased credits do not expire
• Credits are non-refundable except as required by law

Subscriptions:
• Subscriptions auto-renew monthly until cancelled
• Monthly credit allocations do not roll over
• You may cancel at any time; access continues until the billing period ends
• Price changes will be communicated in advance

Refunds:
Refund requests are handled on a case-by-case basis. Contact support for assistance.`,
  },
  {
    id: "privacy",
    title: "8. Privacy",
    shortTitle: "Privacy",
    content: `Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.

For users under 13 years old, we comply with the Children's Online Privacy Protection Act (COPPA). Please see our Privacy Policy for details on how we handle children's information.`,
  },
  {
    id: "termination",
    title: "9. Termination",
    shortTitle: "Termination",
    content: `We may suspend or terminate your account at any time for violation of these Terms or for any other reason at our sole discretion. Upon termination:

• Your right to use the Service ceases immediately
• Your Generated Content may be deleted after a reasonable period
• Any unused credits or subscription time will not be refunded except as required by law

You may delete your account at any time by contacting support.`,
  },
  {
    id: "disclaimers",
    title: "10. Disclaimers and Limitation of Liability",
    shortTitle: "Disclaimers",
    content: `THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.

TO THE MAXIMUM EXTENT PERMITTED BY LAW, MYTHO LABS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.

OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.`,
  },
  {
    id: "indemnification",
    title: "11. Indemnification",
    shortTitle: "Indemnity",
    content: `You agree to indemnify and hold harmless Mytho Labs and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from:

• Your use of the Service
• Your violation of these Terms
• Your violation of any rights of another party
• Your User Content`,
  },
  {
    id: "governing-law",
    title: "12. Governing Law",
    shortTitle: "Law",
    content: `These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.

Any disputes arising under these Terms shall be resolved in the state or federal courts located in Delaware, and you consent to the personal jurisdiction of such courts.`,
  },
  {
    id: "changes",
    title: "13. Changes to Terms",
    shortTitle: "Changes",
    content: `We reserve the right to modify these Terms at any time. We will notify you of material changes by:

• Posting the updated Terms on our website
• Sending an email to your registered address
• Displaying a notice within the Service

Your continued use of the Service after changes become effective constitutes acceptance of the modified Terms.`,
  },
  {
    id: "contact",
    title: "14. Contact Information",
    shortTitle: "Contact",
    content: `If you have questions about these Terms, please contact us:

Mytho Labs
Email: support@mytho-labs.com
Website: https://tellor.mytho-labs.com/contact

For legal notices, please send correspondence to our legal department at the email address above with "Legal Notice" in the subject line.`,
  },
];

/**
 * TermsPage Component
 *
 * Terms of service page with table of contents and sections.
 */
const TermsPage = (): ReactElement => {
  const navigate = useNavigate();
  const [showToc, setShowToc] = useState<boolean>(false);

  const scrollToSection = (id: string): void => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setShowToc(false);
  };

  return (
    <PageLayout>
      <div className="min-h-screen pb-16" style={{ background: "var(--st-bg-page)" }}>
        {/* Back Button */}
        <div className="max-w-3xl mx-auto px-4 pt-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm mb-4 transition-all hover:opacity-70"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {/* Page Header Card */}
        <div className="max-w-3xl mx-auto px-4">
          <div
            className="rounded-2xl p-6 mb-6"
            style={{
              background: "var(--st-bg-surface)",
              border: "1px solid var(--st-border-light)",
              boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className="w-16 h-16 mb-4 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                }}
              >
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h1
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
              >
                Terms of Service
              </h1>
              <p
                className="text-sm"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Last updated: {LAST_UPDATED}
              </p>

              {/* Table of Contents Toggle - Mobile */}
              <button
                onClick={() => setShowToc(!showToc)}
                className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] md:hidden"
                style={{
                  background: "var(--st-bg-page)",
                  border: "1px solid var(--st-border-light)",
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                <List className="w-4 h-4" />
                {showToc ? "Hide" : "Show"} Table of Contents
              </button>
            </div>
          </div>
        </div>

        {/* Mobile TOC Dropdown */}
        {showToc && (
          <div className="max-w-3xl mx-auto px-4 mb-6 md:hidden">
            <div
              className="rounded-xl p-4"
              style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
            >
              <div className="space-y-1">
                {SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all hover:bg-orange-50 dark:hover:bg-orange-950/30"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-heading)",
                    }}
                  >
                    {section.shortTitle}
                    <ChevronRight className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex gap-6">
            {/* Desktop Table of Contents - Sticky Sidebar */}
            <div className="hidden md:block w-48 flex-shrink-0">
              <div
                className="sticky top-24 rounded-xl p-4"
                style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
              >
                <p
                  className="text-xs font-semibold mb-3 uppercase tracking-wide"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-muted)",
                  }}
                >
                  Contents
                </p>
                <div className="space-y-1">
                  {SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="w-full text-left px-2 py-1.5 rounded-lg text-xs transition-all hover:bg-orange-50 dark:hover:bg-orange-950/30"
                      style={{
                        fontFamily: "Quicksand, sans-serif",
                        color: "var(--st-text-heading)",
                      }}
                    >
                      {section.shortTitle}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <div
                className="rounded-xl p-6 md:p-8"
                style={{
                  background: "var(--st-bg-surface)",
                  border: "1px solid var(--st-border-light)",
                  boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
                }}
              >
                {/* Introduction */}
                <p
                  className="text-sm mb-8 leading-relaxed"
                  style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                >
                  Welcome to Mytho Labs. Please read these Terms of Service
                  carefully before using our Service. These Terms govern your access
                  to and use of the Mytho Labs platform and services.
                </p>

                {/* Sections */}
                <div className="space-y-8">
                  {SECTIONS.map((section) => (
                    <div key={section.id} id={section.id} className="scroll-mt-24">
                      <h2
                        className="text-lg font-bold mb-3"
                        style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                      >
                        {section.title}
                      </h2>
                      <div
                        className="text-sm leading-relaxed whitespace-pre-wrap"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-text-heading)",
                        }}
                      >
                        {section.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="max-w-3xl mx-auto px-4 mt-8">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => navigate("/privacy")}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Privacy Policy
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => navigate("/help")}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Help & FAQ
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => navigate("/contact")}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default TermsPage;
